import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';

// ** Spinner (Splash Screen)
import Spinner from '@core/components/spinner/Fallback-spinner';

import {
  changeViewMode,
  getListLanguageAsync,
  getUser,
  logout,
  setTempUserToken,
} from 'store/auth';
import userApi from 'api/userApi';
import { SOCKET_NAMESPACE, USER_ROLES } from '../constants/constants';
import {
  ARR_USER_ROLES,
  TEMP_USER_TOKEN,
  USER_TOKEN,
  USER_VIEW_MODE,
} from '../helpers/storage';
import { useSocket } from '../hooks/useSocket';
import { useFetchSettings } from '../hooks/settings';
import { useTempUserToken, useUser } from '../hooks';
import { setUserUnread } from '../store/messages';
import { DOMAIN_HOST } from '../config';
import { hasCookie, setCookie } from 'cookies-next';

const allowPathnameInstructor = [
  '/',
  '/profile',
  '/lms-user/home',
  '/course/[slug]',
  '/course/[slug]/[type]',
  '/course/[slug]/Asssignment',
  '/course/[slug]/newAssignment',
  '/course/[slug]/add-test',
  '/course/[slug]/DetailTest',
  '/course/[slug]/edit-test',
  '/course/[slug]/add-survey',
  '/course/[slug]/edit-survey',
  '/course/[slug]/users-and-progress',
  '/course/[slug]/files',
  '/course/[slug]/rules-and-path',
  '/course/[slug]/great-book',
  '/course/[slug]/report',
  '/course/[slug]/add-classroom/[unitId]',
  '/course/[slug]/add-classroom/[unitId]/Addclassroom',
  '/course/[slug]/add-classroom/[unitId]/users',
  '/attendance/qr/[token]',
  '/course/[slug]/add-live-training',
  '/course/[slug]/add-live-training/liveClassNew',
  '/course/[slug]/add-live-training/[unitId]',
  '/course/[slug]/add-live-training/[unitId]/Addlivetraining',
  '/course/[slug]/add-live-training/[unitId]/users',
  '/course/[slug]/add-msteams-meeting',
  '/course/[slug]/add-msteams-meeting/[unitId]',
  '/course/[slug]/add-msteams-meeting/[unitId]/users',
  '/calendar',
  '/course/create',
  '/course/[slug]/edit',
  '/learning-path',
  '/learning-path/[learningPathId]',
  '/unit/[id]/edit',
  '/course/[slug]/question/create',
  '/course/[slug]/question/edit',
  '/course/[slug]/submissions/[id]',
  '/group',
  '/group/create',
  '/group/[id]/edit',
  '/course',
  '/course/[slug]/unit/[unitId]',
  '/course/[slug]/discussion',
  '/course/[slug]/discussion/[id]',
  '/messages',
  '/announcement',
  '/announcement/create',
  '/announcement/view',
  '/business-rules',
  '/business-rules/[businessRuleId]',
  '/course/[slug]/whiteboard',
  '/course/[slug]/whiteboard/create',
  '/timesheet/schedule',
  '/timesheet/schedule/view',
  '/timesheet/schedule/calenderview',
  '/timesheet/approve',
  '/timesheet/approve/view',
  '/timesheet/mytimesheet',
  '/timesheet/mytimesheet/view',
  '/timesheet/mytimesheet/addbank',
  '/timesheet/myschedule',
  '/timesheet/myschedule/view',
  '/lms-user/cart/wishList',
  '/monitoring',
  '/intakes',
  '/intakes/create',
  '/intakes/intakeGrid',
  '/intakes/intake-grid',
  '/course/[slug]/course-workshop',
  '/course/[slug]/course-ws-create',
  '/course/[slug]/course-ws-view',
  '/course/[slug]/course-ws-edit',
  '/lms-user/cart',
  '/homes/myLearning',
  '/workshop',
  '/workshop/create',
  '/workshop/view',
  '/workshop/edit',
  '/workshop/workshopCard',
  '/lms-user/instructor',
  '/homes/cart/paymentsuccess',
  '/reports/tests',
  '/reports/tests/[id]',
  '/reports/assignments',
  '/reports/assignments/[id]',
  '/reports/surveys',
  '/reports/surveys/[id]',
  '/reports/surveys/questions',
  '/reports/test/questions',
  '/reports/test/questions/[questionId]',
  '/reports/surveys/questions/[questionId]',
  '/reports/learning-path',
  '/reports/learning-path/[learningPathId]',
  '/reports/allLessonReports',
  '/video-player/[fileId]',
  '/intake/approval',
  '/intake/great-book',
  '/intake/great-book/[intakeId]',
  '/master/resource',
  '/master/resource/create',
  '/master/resource/[resourceId]',
  '/master/location',
  '/master/location/create',
  '/master/location/[locationId]',
  '/master/competency-mastery-levels',
  '/master/competency-mastery-levels/create',
  '/master/competency-mastery-levels/[masterlevelId]',
  '/master/competency-level',
  '/master/competency-level/[competencyLevelId]',
  '/master/core-competencies',
  '/master/core-competencies/create',
  '/master/core-competencies/[competencyId]',
  '/reports/asynchronous-attendance',
  '/reports/great-book/intake-report',
  '/reports/great-book/global-report',
  '/stackable-course',
  '/reports/stackable',
  '/reports/stackable/[stackId]',
  '/course/[slug]/question/ai-create',
];

const allowPathnameCentreAdmin = [
  '/',
  '/profile',
  '/course/courseGrid',
  '/intakes/intakeGrid',
  '/intakes/intake-grid',
  '/course/[slug]/unit/[unitId]',
  '/attendance/qr/[token]',
  '/course/[slug]/discussion',
  '/course/[slug]/discussion/[id]',
  '/course/[slug]/edit',
  '/course/[slug]',
  '/course/[slug]/report',
  '/course/[slug]/files',
  '/announcement',
  '/announcement/create',
  '/announcement/view',
  '/master/points',
  '/master/points/create?type=topUp',
  '/staff-request',
  '/staff-request/[staffRequestId]',
  '/staff-request/[staffRequestId]/edit',
  '/staff-request/[staffRequestId]/view',
  '/stackable-course',
  '/course/[slug]/question/ai-create',
];

const allowPathnameCentreBL = [
  '/',
  '/profile',
  '/master/points',
  '/master/points/create?type=topUp',
  '/users',
  '/course/courseGrid',
  '/intakes/intakeGrid',
  '/intakes/intake-grid',
  '/staff-request',
  '/master/organization',
  '/master/businessType',
  '/master/centreType',
  '/manage-staff-request',
  '/reports',
  '/reports/users',
  '/reports/courses',
  '/reports/liveClass',
  '/reports/calandar',
  '/reports/attendance',
  '/reports/tests',
  '/reports/assignments',
  '/reports/surveys',
  '/reports/credits/creditsGlobal',
  '/reports/learning-path',
  '/certificate/certificateReport?report="Report"',
  '/course/[slug]',
  '/course/[slug]/course-workshop',
  '/course/[slug]/course-ws-view',
  '/course/[slug]/unit/[unitId]',
  '/attendance/qr/[token]',
  '/calendar',
  '/course/[slug]/discussion',
  '/course/[slug]/discussion/[id]',
  '/course/[slug]/question/ai-create',
];

const allowLayoutLearner = [
  '/',
  '/profile',
  '/intakes/intakeGrid',
  '/intakes/intake-grid',
  '/course/[slug]',
  '/course/[slug]/course-workshop',
  '/course/[slug]/course-ws-view',
  '/course/[slug]/unit/[unitId]',
  '/attendance/qr/[token]',
  '/calendar',
  '/course/[slug]/discussion',
  '/course/[slug]/discussion/[id]',
  '/messages',
  '/course/[slug]/files',
  '/course/[slug]/whiteboard',
  '/course/[slug]/whiteboard/create',
  '/workshop',
  '/workshop/view',
  '/lms-user/instructor',
  '/Badge/badge',
  '/learning-path',
  '/learning-path/[learningPathId]',
  '/video-player/[fileId]',
  '/certificate/userCertificate',
  '/certificate/userCertificate/[displayCertificate]',
  '/reports/competency/isLearner',
  '/course/[slug]/question/ai-create',
];

const allowLayoutLearnerCMS = [
  '/',
  '/profile',
  '/intakes',
  '/intakes/intakeGrid',
  '/intakes/intake-grid',
  '/course',
  '/course/[slug]',
  '/course/[slug]/course-workshop',
  '/course/[slug]/course-ws-view',
  '/course/[slug]/unit/[unitId]',
  '/attendance/qr/[token]',
  '/calendar',
  '/course/[slug]/discussion',
  '/course/[slug]/discussion/[id]',
  '/messages',
  '/course/[slug]/files',
  '/course/[slug]/whiteboard',
  '/course/[slug]/whiteboard/create',
  '/lms-user/cart',
  '/lms-user/cart/checkout',
  '/lms-user/home',
  '/lms-user/cart/wishList',
  '/homes/myLearning',
  '/workshop',
  '/workshop/view',
  '/workshop/workshopCard',
  '/homes/purchaseHistory/invoice',
  '/homes/purchaseHistory',
  '/lms-user/instructor',
  '/homes/cart/paymentsuccess',
  '/homes/myLearning/learning',
  '/Badge/modalBadges',
  '/Badge/badge',
  '/certificate/userCertificate',
  '/certificate/userCertificate/[displayCertificate]',
  '/reports/competency/isLearner',
  '/homes/course',
  '/homes/intake/[slug]',
  '/homes/intake',
  '/homes/intake/intakeGridcard',
];

const withAuthUser = (Component) => (props) => {
  const dispatch = useDispatch();
  const route = useRouter();
  const user = useUser();
  const tempUserToken = useTempUserToken();
  const { pathname, asPath } = route;
  const [isLoading, setIsLoading] = useState(true);
  const metaData = useSelector((state) => state.settingAccount.metaData);

  useSocket(SOCKET_NAMESPACE.CHAT);
  useFetchSettings();
  async function detectUser(goHome = false) {
    try {
      const userLoggedIn = await userApi.get();
      if (USER_TOKEN.get()) {
        if (!hasCookie('userToken')) {
          setCookie('userToken', USER_TOKEN.get());
        }
        setCookie('userRoles', userLoggedIn?.roles);
        setCookie(
          'userViewMode',
          userLoggedIn?.type?.name === USER_ROLES.SUPER_ADMIN
            ? USER_ROLES.ADMIN
            : userLoggedIn?.type?.defaultRole,
        );
      }
      if (typeof userLoggedIn?.unreadMessage === 'number') {
        dispatch(setUserUnread(userLoggedIn?.unreadMessage));
      }
      const viewMode = await USER_VIEW_MODE.get();
      if (viewMode && userLoggedIn?.roles?.indexOf(viewMode) !== -1) {
        dispatch(changeViewMode(viewMode));
        if (viewMode === USER_ROLES.INSTRUCTOR) {
          if (allowPathnameInstructor.indexOf(pathname) === -1 || goHome) {
            await route.replace('/', null, { shallow: true });
          }
        } else if (viewMode === USER_ROLES.CENTRE_ADMIN) {
          if (!allowPathnameCentreAdmin.includes(asPath)) {
            if (allowPathnameCentreAdmin.indexOf(pathname) === -1 || goHome) {
              await route.replace('/', null, { shallow: true });
            }
          }
        } else if (viewMode === USER_ROLES.CENTRE_BL) {
          if (!allowPathnameCentreBL.includes(asPath)) {
            if (allowPathnameCentreBL.indexOf(pathname) === -1 || goHome) {
              await route.replace('/', null, { shallow: true });
            }
          }
        } else if (viewMode !== USER_ROLES.ADMIN) {
          if (
            metaData?.cmsHomePage
              ? allowLayoutLearnerCMS.indexOf(pathname) === -1
              : allowLayoutLearner.indexOf(pathname) === -1 || goHome
          ) {
            await route.replace('/', null, { shallow: true });
          }
        }
      } else {
        // first login
        if (
          userLoggedIn?.roles?.indexOf(USER_ROLES.SUPER_ADMIN) !== -1 ||
          userLoggedIn?.roles?.indexOf(USER_ROLES.ADMIN) !== -1
        ) {
          dispatch(changeViewMode(USER_ROLES.ADMIN));
        } else if (userLoggedIn?.roles?.indexOf(USER_ROLES.INSTRUCTOR) !== -1) {
          dispatch(changeViewMode(USER_ROLES.INSTRUCTOR));
          if (allowPathnameInstructor.indexOf(pathname) === -1 || goHome) {
            await route.replace('/', null, { shallow: true });
          }
        } else if (
          userLoggedIn?.roles?.indexOf(USER_ROLES.CENTRE_ADMIN) !== -1
        ) {
          dispatch(changeViewMode(USER_ROLES.CENTRE_ADMIN));
          if (allowPathnameCentreAdmin.indexOf(pathname) === -1 || goHome) {
            await route.replace('/', null, { shallow: true });
          }
        } else if (userLoggedIn?.roles?.indexOf(USER_ROLES.CENTRE_BL) !== -1) {
          dispatch(changeViewMode(USER_ROLES.CENTRE_BL));
          if (allowPathnameCentreAdmin.indexOf(pathname) === -1 || goHome) {
            await route.replace('/', null, { shallow: true });
          }
        } else {
          if (userLoggedIn?.roles?.indexOf(USER_ROLES.LEARNER) !== -1) {
            dispatch(changeViewMode(USER_ROLES.LEARNER));
          }
          if (
            // metaData?.cmsHomePage
            allowLayoutLearnerCMS.indexOf(pathname) === -1
            // : allowLayoutLearner.indexOf(pathname) === -1 || goHome
          ) {
            await route.replace('/', null, { shallow: true });
          }
        }
      }
      dispatch(getUser(userLoggedIn));
      setIsLoading(false);
    } catch (error) {
      if (TEMP_USER_TOKEN.get()) {
        await dispatch(setTempUserToken(undefined));
        await USER_VIEW_MODE.delete();
        await detectUser();
      } else {
        if (pathname === '/attendance/qr/[token]') {
          await route.replace({
            pathname: metaData?.cmsHomePage ? '/loginCms' : '/login',
            query: { redirect: asPath },
          });
          return;
        }

        if (metaData?.cmsHomePage) {
          await route.replace('/homeCms');
        } else {
          await route.replace('/home');
        }
      }
    }
  }

  const authUser = async () => {
    window.addEventListener('storage', (e) => {
      if (e.key === 'userToken' && e.oldValue && !e.newValue) {
        if (metaData?.cmsHomePage) {
          route.push('/loginCms').then(() => {
            dispatch(logout());
          });
        } else {
          route.push('/login').then(() => {
            dispatch(logout());
          });
        }
      }
    });
    try {
      const tempUserToken = TEMP_USER_TOKEN.get();
      if (tempUserToken) {
        await dispatch(setTempUserToken(tempUserToken));
      }
      await dispatch(getListLanguageAsync());
      await detectUser();
    } catch (error) {
      await route.push('/home');
    }
  };

  const listenChangeStorage = async (event) => {
    if (event.key === 'userViewMode') {
      if (event.newValue !== event.oldValue) {
        const arrRole = ARR_USER_ROLES.get()?.split(',');
        if (arrRole && arrRole?.indexOf(event.newValue) !== -1) {
          await dispatch(changeViewMode(event.newValue));
          await route.reload();
        }
      }
    }
    if (event.key === 'tempUserToken') {
      if (event.newValue !== event.oldValue) {
        if (event.newValue) {
          // await detectUser(true);
          await dispatch(setTempUserToken(event.newValue));
        }
        await USER_VIEW_MODE.delete();
        window.open(DOMAIN_HOST, '_self');
      }
    }
  };

  useEffect(() => {
    if (metaData) {
      authUser();
      window.addEventListener('storage', listenChangeStorage);
      return () => {
        window.removeEventListener('storage', listenChangeStorage);
      };
    }
  }, [metaData]);

  useEffect(() => {
    const detectChangeToken = async () => {
      if (user?._id) {
        // await detectUser(true);
        // window.open(DOMAIN_HOST, '_self');
      }
    };
    detectChangeToken();
  }, [tempUserToken]);

  if (isLoading) return <Spinner />;

  return <Component {...props} />;
};

export default withAuthUser;
